/**
 * Benefit plan group operations
 *
 * Low-level API client functions for benefit plan group management.
 * These functions handle the actual HTTP requests or mock implementations.
 * They are consumed by React Query hooks in mutations.ts and queries.ts.
 */

import { PlanOptionNameEnum } from "@platform/openapi-types";
import { logger } from "@platform/browser-logger";
import { AxiosError, AxiosInstance } from "axios";

import {
  BenefitPlanGroup,
  CandidatePoliciesResponse,
  CandidatePolicy,
  CreateBenefitPlanGroupRequest,
  DuplicateBenefitPlanGroupRequest,
  ListBenefitPlanGroupsParams,
  ListBenefitPlanGroupsResponse,
  ReorderBenefitPlanGroupsRequest,
  ReorderBenefitPlanGroupsResponse,
  ReorderBenefitPlansInGroupRequest,
  ReorderBenefitPlansInGroupResponse,
  UpdateBenefitPlanGroupRequest,
  UpdateBenefitPlanGroupResponse,
} from "./types";

// Base URL for benefit plan groups APIs
const BENEFIT_PLAN_GROUPS_BASE_URL = "/btx/v1/benefit-plan-groups";

// Base URL for coverage APIs (candidate-policies is coverage-scoped, ADR 10.22)
const COVERAGES_BASE_URL = "/btx/v1/coverages";

/**
 * Fetches the active in-force broker policies selectable as a current plan
 * group's Current Carrier - Policy (ADR 10.22). Coverage-scoped so the
 * Add-New-Plan-Group flow can call it before a group exists.
 *
 * Uses BTX endpoint: GET /api/btx/v1/coverages/{coverageId}/candidate-policies
 */
export const getCandidatePolicies = async (
  coverageId: string,
  planType: string | undefined,
  axiosInstance: AxiosInstance,
): Promise<CandidatePolicy[]> => {
  const query = planType
    ? `?${new URLSearchParams({ planType }).toString()}`
    : "";

  const response = await axiosInstance.get<CandidatePoliciesResponse>(
    `${COVERAGES_BASE_URL}/${coverageId}/candidate-policies${query}`,
  );

  return response.data.policies || [];
};

/**
 * Lists benefit plan groups for an RFP and coverage type
 *
 * Uses BTX endpoint: GET /api/btx/v1/benefit-plan-groups
 *
 * @param params - Query parameters for listing benefit plan groups
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to array of benefit plan groups
 */
export const listBenefitPlanGroups = async (
  params: ListBenefitPlanGroupsParams,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroup[]> => {
  const queryParams = new URLSearchParams({
    rfpId: params.rfpId,
  });

  if (params.coverageType) {
    queryParams.append("coverageType", params.coverageType);
  }

  if (params.productType) {
    queryParams.append("productType", params.productType);
  }

  const response = await axiosInstance.get<ListBenefitPlanGroupsResponse>(
    `${BENEFIT_PLAN_GROUPS_BASE_URL}?${queryParams.toString()}`,
  );

  return response.data.benefitPlanGroups || [];
};

/**
 * Creates a new benefit plan group
 *
 * Uses BTX endpoint: POST /api/btx/v1/benefit-plan-groups
 *
 * @param request - Create benefit plan group request
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to created benefit plan group
 */
export const createBenefitPlanGroup = async (
  request: CreateBenefitPlanGroupRequest | DuplicateBenefitPlanGroupRequest,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroup> => {
  const response = await axiosInstance.post<BenefitPlanGroup>(
    BENEFIT_PLAN_GROUPS_BASE_URL,
    request,
  );

  return response.data;
};

/**
 * Deletes a benefit plan group
 *
 * Uses BTX endpoint: DELETE /api/btx/v1/benefit-plan-groups/:id
 *
 * @param id - Benefit plan group ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteBenefitPlanGroup = async (
  id: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(`${BENEFIT_PLAN_GROUPS_BASE_URL}/${id}`);
};

/**
 * Deletes a single benefit plan (plan-level column).
 *
 * Uses Plan Builder endpoint: DELETE /plan-builder/:rfpId/benefit-plans/:benefitPlanId
 *
 * @param rfpId - RFP ID
 * @param benefitPlanId - Benefit plan ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteBenefitPlan = async (
  rfpId: string,
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(
    `/gateway/plan-builder/${encodeURIComponent(rfpId)}/benefit-plans/${encodeURIComponent(benefitPlanId)}`,
  );
};

/**
 * Updates a benefit plan group (nickname and/or size)
 *
 * @param request - Update request with group ID and new values
 * @param axiosInstance - Optional axios instance for real API calls
 * @returns Promise resolving to update response
 *
 * @example
 * ```ts
 * const response = await updateBenefitPlanGroup({
 *   id: "uuid-group-1",
 *   nickname: "Premium Plans",
 *   size: 3
 * }, axiosInstance);
 * ```
 */
export const updateBenefitPlanGroup = async (
  request: UpdateBenefitPlanGroupRequest,
  axiosInstance: AxiosInstance,
): Promise<UpdateBenefitPlanGroupResponse> => {
  try {
    const response = await axiosInstance.patch(
      `${BENEFIT_PLAN_GROUPS_BASE_URL}/${request.id}`,
      {
        ...(request.nickname !== undefined && { nickname: request.nickname }),
        ...(request.size !== undefined && { size: request.size }),
        // null clears the reference; a string sets it; omit to leave unchanged
        // (matches the backend's presence-aware handling, ADR 10.22).
        ...(request.clearCurrentPolicy
          ? { currentPolicyUuid: null }
          : request.currentPolicyUuid !== undefined && {
              currentPolicyUuid: request.currentPolicyUuid,
            }),
      },
    );

    return response.data as BenefitPlanGroup;
  } catch (error: unknown) {
    const apiError = error as AxiosError<{ message?: string }>;
    logger.error("[API] Error updating benefit plan group:", apiError);
    throw new Error(
      apiError.response?.data?.message ||
        `Failed to update benefit plan group: ${apiError.message}`,
    );
  }
};

/**
 * Reorders benefit plan groups by position
 *
 * @param request - Reorder request with RFP context and ordered group IDs
 * @param axiosInstance - Optional axios instance for real API calls
 * @returns Promise resolving to reorder response
 *
 * @example
 * ```ts
 * const response = await reorderBenefitPlanGroups({
 *   rfpId: "rfp-123",
 *   coverageType: "dental",
 *   productType: "ppo",
 *   benefitPlanGroups: [
 *     { id: "uuid-group-2" },
 *     { id: "uuid-group-1" },
 *     { id: "uuid-group-3" }
 *   ]
 * }, axiosInstance);
 * ```
 */
export const reorderBenefitPlanGroups = async (
  request: ReorderBenefitPlanGroupsRequest,
  axiosInstance: AxiosInstance,
): Promise<ReorderBenefitPlanGroupsResponse> => {
  try {
    const queryParams = new URLSearchParams({
      rfpId: request.rfpId,
      coverageType: request.coverageType,
      productType: request.productType,
    });

    const response = await axiosInstance.patch(
      `/api${BENEFIT_PLAN_GROUPS_BASE_URL}?${queryParams.toString()}`,
      {
        benefitPlanGroups: request.benefitPlanGroups,
      },
    );

    return (response.data || []) as BenefitPlanGroup[];
  } catch (error: unknown) {
    const apiError = error as AxiosError<{ message?: string }>;
    logger.error("[API] Error reordering benefit plan groups:", apiError);
    throw new Error(
      apiError.response?.data?.message ||
        `Failed to reorder benefit plan groups: ${apiError.message}`,
    );
  }
};

/**
 * Reorders the plans within a single benefit plan group.
 *
 * Uses BTX endpoint: PATCH /btx/v1/benefit-plan-groups/:id/plans/reorder
 *
 * @param request - Group ID and the plans' new left-to-right order
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to the reordered group (with regenerated identifiers)
 */
export const reorderBenefitPlansInGroup = async (
  request: ReorderBenefitPlansInGroupRequest,
  axiosInstance: AxiosInstance,
): Promise<ReorderBenefitPlansInGroupResponse> => {
  try {
    const response = await axiosInstance.patch(
      `${BENEFIT_PLAN_GROUPS_BASE_URL}/${encodeURIComponent(
        request.groupId,
      )}/plans/reorder`,
      {
        orderedIds: request.orderedIds,
      },
    );

    return response.data as BenefitPlanGroup;
  } catch (error: unknown) {
    const apiError = error as AxiosError<{ message?: string }>;
    logger.error("[API] Error reordering benefit plans in group:", apiError);
    throw new Error(
      apiError.response?.data?.message ||
        `Failed to reorder benefit plans in group: ${apiError.message}`,
    );
  }
};

/**
 * Duplicates a benefit plan within its group.
 *
 * Uses Plan Builder endpoint: POST /plan-builder/benefit-plans/:id/duplicate
 *
 * @param benefitPlanId - Benefit plan ID to duplicate
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to the updated benefit plan group
 */
export const duplicateBenefitPlan = async (
  benefitPlanId: string,
  axiosInstance: AxiosInstance,
): Promise<BenefitPlanGroup> => {
  const response = await axiosInstance.post(
    `/btx/v1/benefit-plans/${encodeURIComponent(benefitPlanId)}/duplicate`,
  );

  return response.data as BenefitPlanGroup;
};

/**
 * Updates a benefit plan's option name.
 *
 * Uses BTX endpoint: PATCH /btx/v1/benefit-plans/:id
 */
export const updateBenefitPlanOptionName = async (
  benefitPlanId: string,
  optionName: PlanOptionNameEnum,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.patch(
    `/btx/v1/benefit-plans/${encodeURIComponent(benefitPlanId)}`,
    { optionName },
  );
};
